import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { Constants } from 'src/app/models/constants';
import { QRButton } from 'src/app/models/qr-button.model';
import { QRIcon } from 'src/app/models/qr-icon.model';
import { QRInput } from 'src/app/models/qr-input.model';
import { debounceTime } from 'rxjs/operators';
import { environment } from '@base/environments/environment';
import {
  handleDecimalNumberKeydown,
  handleDecimalNumberPaste,
} from 'src/app/pages/accounts/utils/locale-number-input.util';

export type InputTypes = string | number | undefined;

@Component({
  selector: 'qr-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
})
export class InputComponent implements OnInit {
  @Input() config!: QRInput;
  @Input() isDisabled?: boolean = false;
  @Input() hasError?: boolean = false;
  @Input() restrictDecimalInput?: boolean = false;

  @ViewChild('nativeInput') nativeInput?: ElementRef<HTMLInputElement>;

  @Input() value?: InputTypes;
  visibleValue: InputTypes; // Valor que es visible para el usuario final con formato

  valueSubject: Subject<InputTypes> = new Subject();
  @Output() valueChange: EventEmitter<InputTypes> =
    new EventEmitter<InputTypes>();
  @Output() blurChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() focusIn: EventEmitter<boolean> = new EventEmitter<boolean>();

  buttonClear: QRButton = {
    hierarchy: QRButton.HIERARCHY_4,
    size: QRButton.SIZE_SMALL,
    icon: Constants.ICONS.CLOSE,
    iconOnly: true,
  };

  buttonTogglePassword: QRButton = {
    hierarchy: QRButton.HIERARCHY_4,
    size: QRButton.SIZE_SMALL,
    icon: Constants.ICONS.EYE_OPEN,
    iconOnly: true,
  };

  ngOnInit(): void {
    this.valueSubject
      .pipe(debounceTime(this.config.debounceTime ?? 0))
      .subscribe((value: InputTypes) => {
        // Modo cadena de textos
        if (this.config.mode == QRInput.MODE_STRING) {
          if (this.value == undefined || this.value.toString().length == 0) {
            this.value = undefined;
          }
          this.valueChange.emit(this.value);
        }
        // Modo solo números enteros
        if (this.config.mode == QRInput.MODE_INTEGER) {
          if (this.value != undefined) {
            this.value = Number(this.value);
          } else {
            this.value = undefined;
          }
          this.valueChange.emit(this.value);
        }
        // Modo números decimales
        if (this.config.mode == QRInput.MODE_DECIMAL) {
          if (this.value != undefined) {
            this.value = Number(this.value);
          } else {
            this.value = undefined;
          }
          this.valueChange.emit(this.value);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes['value']) {
      return;
    }

    const NATIVE = this.nativeInput?.nativeElement;
    if (NATIVE && document.activeElement === NATIVE) {
      return;
    }

    const NEXT_VALUE = changes['value'].currentValue;
    if (NEXT_VALUE?.toString() === this.visibleValue?.toString()) {
      return;
    }

    this.modelChange(this.value);
  }

  ngOnDestroy(): void {
    this.valueSubject.unsubscribe();
  }

  modelChange(value: InputTypes): void {
    this.value = value;
    if (this.config.mode == QRInput.MODE_INTEGER) {
      if (this.value?.toString().length != 0) {
        this.value = this.returnOnlyNumber(value?.toString());
        this.integerNumberFormatVisibleValue(this.value);
      } else {
        this.value = undefined;
      }
    }

    if (this.config.mode == QRInput.MODE_DECIMAL) {
      if (this.value?.toString().length != 0) {
        this.decimalNumberformatValue(this.value);
      } else {
        this.value = undefined;
      }
    }

    if (this.config.mode == QRInput.MODE_STRING) {
      if (value != null && this.config.allowedPattern) {
        const FILTERED = value
          .toString()
          .split('')
          .filter((char) => this.config.allowedPattern!.test(char))
          .join('');
        this.value = FILTERED;
        this.visibleValue = FILTERED;
      } else {
        this.visibleValue = value;
      }
    }

    this.valueSubject.next(this.value);
  }

  /*------------------------------------------------------------*/
  /*------------------------------------------------------------*/
  /* isIntegerNumbersModeEnable */
  /*------------------------------------------------------------*/
  /*------------------------------------------------------------*/

  /*
  returnOnlyNumber: Recibe un string y devuelve solo los valores numéricos
  */
  returnOnlyNumber(value: string | undefined): number {
    let number = 0;
    number = Number(value?.replace(/\D/g, ''));
    if (value == undefined) {
      number = 0;
    }
    return number;
  }

  /*
	integerNumberFormatVisibleValue: Darle formato al valor visible
	*/
  integerNumberFormatVisibleValue(value: number | undefined) {
    if (value !== null || value !== undefined) {
      this.visibleValue = this.numberWithDots(value) ?? '0';
    } else {
      this.visibleValue = undefined;
    }
  }

  /*
	numberWithDots: Agregar al valor los puntos de miles
	*/
  numberWithDots(x: InputTypes): string | undefined {
    return x ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') : undefined;
  }

  keydown(e: KeyboardEvent): void {
    if (this.restrictDecimalInput) {
      handleDecimalNumberKeydown(
        e,
        environment.node ?? environment.tenant_id ?? ''
      );
      return;
    }

    if (this.config.mode == QRInput.MODE_INTEGER) {
      this.isValidIntegerNumber(e);
    }
    if (this.config.mode == QRInput.MODE_DECIMAL) {
      this.isValidDecimalNumber(e);
    }
    if (this.config.allowedPattern) {
      this.isValidAllowedPattern(e);
    }
  }

  isValidAllowedPattern(e: KeyboardEvent): void {
    if (
      (e.key === 'a' && e.ctrlKey === true) ||
      (e.key === 'c' && e.ctrlKey === true) ||
      (e.key === 'v' && e.ctrlKey === true) ||
      (e.key === 'x' && e.ctrlKey === true) ||
      (e.key === 'a' && e.metaKey === true) ||
      (e.key === 'c' && e.metaKey === true) ||
      (e.key === 'v' && e.metaKey === true) ||
      (e.key === 'x' && e.metaKey === true) ||
      e.key === 'Tab' ||
      e.key === 'Backspace' ||
      e.key === 'Delete' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight'
    ) {
      return;
    }

    if (e.key.length !== 1) {
      return;
    }

    if (!this.config.allowedPattern?.test(e.key)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  onDecimalPaste(event: ClipboardEvent): void {
    if (this.restrictDecimalInput) {
      handleDecimalNumberPaste(
        event,
        environment.node ?? environment.tenant_id ?? ''
      );
    }
  }

  isValidIntegerNumber(e: KeyboardEvent): void {
    // Excepciones
    if (
      (e.key === 'a' && e.ctrlKey === true) || // Permitir: Ctrl+A
      (e.key === 'c' && e.ctrlKey === true) || // Permitir: Ctrl+C
      (e.key === 'v' && e.ctrlKey === true) || // Permitir: Ctrl+V
      (e.key === 'x' && e.ctrlKey === true) || // Permitir: Ctrl+X
      (e.key === 'a' && e.metaKey === true) || // Permitir+A (Mac)
      (e.key === 'c' && e.metaKey === true) || // Permitir: Cmd+C (Mac)
      (e.key === 'v' && e.metaKey === true) || // Permitir: Cmd+V (Mac)
      (e.key === 'x' && e.metaKey === true) || // Permitir: Cmd+X (Mac)
      e.key === 'Tab' || // Permitir: Tab
      e.key === 'Backspace' || // Permitir: Backspace
      e.key === 'Delete' || // Permitir: Delete
      e.key === 'ArrowLeft' || // Permitir: Flecha a la izquierda
      e.key === 'ArrowRight' // Permitir: Flecha a la derecha
    ) {
      return; // dejar que pase, no hago nada
    }

    // Asegurarse que es un número valido
    if (e.key === ' ' || isNaN(Number(e.key))) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  }

  /*------------------------------------------------------------*/
  /*------------------------------------------------------------*/
  /* isDecimalNumbersModeEnable */
  /*------------------------------------------------------------*/
  /*------------------------------------------------------------*/

  decimalNumberformatValue(value: InputTypes): void {
    let temporalValue = value?.toString();
    if (this.visibleValue == undefined) {
      temporalValue = temporalValue?.replaceAll('.', ',');
    } else {
      temporalValue = temporalValue?.toString().replaceAll('.', ''); // Limpiar puntos
    }

    // Value ya tiene punto, o sea es un decimal
    if (temporalValue?.toString().includes(',')) {
      let valuesArray = temporalValue?.toString().split(','); // separar los valores entre parte entera y parte decimal
      let valueIntegerPart = valuesArray[0]; // parte de valor entero
      let valueDecimalPart = valuesArray[1]; // parte de valor decimal
      if (valueDecimalPart.length > 2) {
        // Tiene más de dos decimales, lo limito a dos
        valueDecimalPart = valueDecimalPart.slice(0, 2);
      }
      valueDecimalPart = valueDecimalPart.replace(',', ''); // Remplazar coma por punto
      temporalValue = valueIntegerPart + '.' + valueDecimalPart;
    }

    this.value = temporalValue;
    this.decimalNumberFormatVisibleValue(this.value);
  }

  decimalNumberFormatVisibleValue(value: InputTypes): void {
    let temporalValue = value;
    // Value tiene punto, o sea es un decimal
    if (temporalValue?.toString().includes('.')) {
      let valuesArray = temporalValue?.toString().split('.'); // separar los valores entre parte entera y parte decimal
      let valueIntegerPart: string | undefined = valuesArray[0]; // parte de valor entero
      if (Number(valueIntegerPart) > 999) {
        // Es mayor a 999, necesita punto/s de miles
        valueIntegerPart = this.numberWithDots(valueIntegerPart);
      }
      let valueDecimalPart = valuesArray[1]; // parte de valor decimal
      if (valueDecimalPart.length > 2) {
        // Tiene más de dos decimales, lo limito a dos
        valueDecimalPart = valueDecimalPart.slice(0, 2);
      }
      setTimeout(() => {
        this.visibleValue = valueIntegerPart + ',' + valueDecimalPart;
      }, 10);
    } else {
      // Value no tiene punto, es un entero
      let valueIntegerPart = temporalValue;

      if (Number(valueIntegerPart) > 999) {
        // Es mayor a 999, necesita punto/s de miles
        valueIntegerPart = this.numberWithDots(valueIntegerPart);
      }
      this.visibleValue = valueIntegerPart;
    }
  }

  isValidDecimalNumber(e: KeyboardEvent): void {
    // Excepciones
    if (
      (e.key === 'a' && e.ctrlKey === true) || // Permitir: Ctrl+A
      (e.key === 'c' && e.ctrlKey === true) || // Permitir: Ctrl+C
      (e.key === 'v' && e.ctrlKey === true) || // Permitir: Ctrl+V
      (e.key === 'x' && e.ctrlKey === true) || // Permitir: Ctrl+X
      (e.key === 'a' && e.metaKey === true) || // Permitir+A (Mac)
      (e.key === 'c' && e.metaKey === true) || // Permitir: Cmd+C (Mac)
      (e.key === 'v' && e.metaKey === true) || // Permitir: Cmd+V (Mac)
      (e.key === 'x' && e.metaKey === true) || // Permitir: Cmd+X (Mac)
      e.key === 'Tab' || // Permitir: Tab
      e.key === 'Backspace' || // Permitir: Backspace
      e.key === 'Delete' || // Permitir: Delete
      e.key === 'ArrowLeft' || // Permitir: Flecha a la izquierda
      e.key === 'ArrowRight' || // Permitir: Flecha a la derecha
      e.key === ',' // Permitir: Comma
    ) {
      return; // dejar que pase, no hago nada
    }

    // Asegurarse que es un número valido
    if (e.key === ' ' || isNaN(Number(e.key))) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  }

  focusout(e: FocusEvent): void {
    if (this.config.mode == QRInput.MODE_DECIMAL && this.visibleValue) {
      // Cuando esta activado el modo decimal si el usuario dejo al final una coma sin numeros decimales, se elimina la coma ej: "10," --> transforma a "10"
      const lastStringValidation =
        this.visibleValue.toString()[this.visibleValue?.toString().length - 1];
      if (lastStringValidation == ',') {
        this.visibleValue = this.visibleValue.toString().replace(',', '');
      }

      // Cuando esta activado el modo decimal si el usuario dejo al principio una coma, se agrega un 0 ej: ",55" --> transforma a "0,55"
      const firstStringValidation = this.visibleValue.toString()[0];
      if (firstStringValidation == ',') {
        this.visibleValue = this.visibleValue.toString().replace(',', '0,');
      }
    }
  }

  /*------------------------------------------------------------*/
  /*------------------------------------------------------------*/
  /* Icono  */
  /*------------------------------------------------------------*/
  /*------------------------------------------------------------*/

  getIconColor(): string {
    let color: string = QRIcon.COLOR_GREY_700;
    if (!this.isDisabled) {
      color = QRIcon.COLOR_GREY_700;
    }
    if (this.hasError) {
      color = QRIcon.COLOR_ERROR_900;
    }
    if (this.isDisabled && !this.hasError) {
      color = QRIcon.COLOR_GREY_400;
    }
    return color;
  }

  /*------------------------------------------------------------*/
  /*------------------------------------------------------------*/
  /* Limpiar input  */
  /*------------------------------------------------------------*/
  /*------------------------------------------------------------*/

  clear(): void {
    if (!this.isDisabled) {
      this.visibleValue = undefined;
      this.value = undefined;
      this.valueChange.emit(this.value);
    }
  }

  togglePassword(): void {
    if (this.config.type == QRInput.TYPE_TEXT) {
      this.config.type = QRInput.TYPE_PASSWORD;
      this.buttonTogglePassword = {
        ...this.buttonTogglePassword,
        icon: Constants.ICONS.VISIBILITY_OFF,
      };
    } else {
      this.config.type = QRInput.TYPE_TEXT;
      this.buttonTogglePassword = {
        ...this.buttonTogglePassword,
        icon: Constants.ICONS.EYE_OPEN,
      };
    }
  }
}
